import React from 'react';
import PropTypes from 'prop-types';
import { isVideoFile, isYouTubeUrl, parseYouTubeId } from '../utils/mediaUtils';

function ImageModal({ isOpen, imageSrc, imageAlt, onClose, images = [], currentIndex = 0, onPrevious, onNext }) {
  const handleBackdropClick = (e) => {
    const isBackdrop = e.target === e.currentTarget;
    if (isBackdrop) {
      onClose();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      onClose();
    } else if (e.key === 'ArrowLeft' && onPrevious) {
      onPrevious();
    } else if (e.key === 'ArrowRight' && onNext) {
      onNext();
    }
  };

  const hasMultipleImages = images.length > 1;
  // 改為循環模式：有多張時左右箭頭永遠可用
  const canGoPrevious = hasMultipleImages;
  const canGoNext = hasMultipleImages;

  // 以 images[currentIndex] 為主顯示；若未提供 images 或索引不合法，退回傳入的 imageSrc/imageAlt
  const computedImage = images && images[currentIndex] ? images[currentIndex] : null;
  const displaySrc = computedImage && computedImage.image ? computedImage.image : imageSrc;
  const displayAlt = computedImage && (computedImage.title || computedImage.alt) ? (computedImage.title || computedImage.alt) : imageAlt;

  const scrollStateRef = React.useRef({ y: 0, prevBody: {}, prevHtmlScrollBehavior: '' });

  React.useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);

      // 記錄當前滾動位置與原始樣式
      const y = window.pageYOffset || window.scrollY || 0;
      const body = document.body;
      scrollStateRef.current.y = y;
      scrollStateRef.current.prevBody = {
        position: body.style.position,
        top: body.style.top,
        left: body.style.left,
        right: body.style.right,
        overflow: body.style.overflow,
        width: body.style.width,
        paddingRight: body.style.paddingRight,
      };
      scrollStateRef.current.prevHtmlScrollBehavior = document.documentElement.style.scrollBehavior || '';

      // 鎖定滾動並避免因捲軸寬度造成版面跳動
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      body.style.position = 'fixed';
      body.style.left = '0';
      body.style.right = '0';
      body.style.top = `-${y}px`;
      body.style.width = '100%';
      body.style.overflow = 'hidden';
      if (scrollbarWidth > 0) {
        body.style.paddingRight = `${scrollbarWidth}px`;
      }
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);

      // 從 body.style.top 回推出正確位置（更穩健）
      const body = document.body;
      const topStr = body.style.top;
      let y = scrollStateRef.current.y || 0;
      if (topStr) {
        const parsed = parseInt(topStr, 10);
        if (!Number.isNaN(parsed)) y = Math.max(0, -parsed);
      }

      // 還原 body 樣式
      const prev = scrollStateRef.current.prevBody || {};
      body.style.position = prev.position || '';
      body.style.top = prev.top || '';
      body.style.left = prev.left || '';
      body.style.right = prev.right || '';
      body.style.overflow = prev.overflow || '';
      body.style.width = prev.width || '';
      body.style.paddingRight = prev.paddingRight || '';

      // 強制使用非平滑滾動回到原位，避免因 CSS scroll-behavior 造成動畫
      const html = document.documentElement;
      const prevBehavior = scrollStateRef.current.prevHtmlScrollBehavior || '';
      html.style.scrollBehavior = 'auto';

      // 等下一幀（甚至雙幀）之後再復位，避免樣式還原當下的重排把位置覆蓋
      const restoreScroll = () => {
        window.scrollTo({ top: y, left: 0, behavior: 'auto' });
        // 還原原先的 scroll-behavior 設定
        html.style.scrollBehavior = prevBehavior;
      };
      if (typeof window.requestAnimationFrame === 'function') {
        requestAnimationFrame(() => requestAnimationFrame(restoreScroll));
      } else {
        setTimeout(restoreScroll, 0);
      }
    };
  }, [isOpen]);

  React.useEffect(() => {}, [currentIndex, isOpen, images]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed top-0 left-0 w-screen h-screen bg-black bg-opacity-75 z-[9999]"
      onClick={handleBackdropClick}
      style={{ width: '100vw', height: '100vh', overflow: 'hidden' }}
    >
      {/* 關閉按鈕 - 絕對定位在安全區域 */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 text-white text-2xl hover:text-gray-300 transition-colors z-20 bg-black bg-opacity-50 rounded-full"
        style={{ 
          width: '32px', 
          height: '32px', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          top: '8px',
          right: '8px'
        }}
        aria-label="關閉"
      >
        ×
      </button>

      {/* 圖片容器 - 中央區域，為控制按鈕留出邊距 */}
      <div 
        className="absolute flex items-center justify-center"
        onClick={handleBackdropClick}
        style={{
          top: '50px',
          left: '20px', 
          right: '20px',
          bottom: hasMultipleImages ? '60px' : '20px',
          width: 'calc(100vw - 40px)',
          height: hasMultipleImages ? 'calc(100vh - 110px)' : 'calc(100vh - 70px)'
        }}
      >
        {/* 左箭頭 - 絕對定位在左側邊緣 */}
        {hasMultipleImages && onPrevious && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              if (canGoPrevious) onPrevious();
            }}
            disabled={!canGoPrevious}
            className={`absolute left-0 top-1/2 transform -translate-y-1/2 text-white hover:text-gray-300 transition-all duration-300 z-10 bg-black bg-opacity-40 rounded-full border border-white border-opacity-20 ${
              !canGoPrevious ? 'opacity-30 cursor-not-allowed' : 'hover:bg-opacity-70 hover:scale-110 hover:border-opacity-40'
            }`}
            style={{ 
              width: window.innerWidth >= 768 ? '56px' : '36px', 
              height: window.innerWidth >= 768 ? '56px' : '36px', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              left: '0px',
              fontSize: window.innerWidth >= 768 ? '32px' : '20px',
              fontWeight: 'bold',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)'
            }}
            aria-label="上一張圖片"
            onMouseDown={(e) => e.stopPropagation()}
          >
            ‹
          </button>
        )}

        {/* 右箭頭 - 絕對定位在右側邊緣 */}
        {hasMultipleImages && onNext && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              if (canGoNext) onNext();
            }}
            disabled={!canGoNext}
            className={`absolute right-0 top-1/2 transform -translate-y-1/2 text-white hover:text-gray-300 transition-all duration-300 z-10 bg-black bg-opacity-40 rounded-full border border-white border-opacity-20 ${
              !canGoNext ? 'opacity-30 cursor-not-allowed' : 'hover:bg-opacity-70 hover:scale-110 hover:border-opacity-40'
            }`}
            style={{ 
              width: window.innerWidth >= 768 ? '56px' : '36px', 
              height: window.innerWidth >= 768 ? '56px' : '36px', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              right: '0px',
              fontSize: window.innerWidth >= 768 ? '32px' : '20px',
              fontWeight: 'bold',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)'
            }}
            aria-label="下一張圖片"
            onMouseDown={(e) => e.stopPropagation()}
          >
            ›
          </button>
        )}

        {/* 媒體顯示 - 依類型切換 */}
        {isYouTubeUrl(displaySrc) ? (
          <iframe
            title={displayAlt || "YouTube 影片"}
            className="object-contain rounded-lg shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            src={(function () {
              const id = parseYouTubeId(displaySrc);
              return id ? `https://www.youtube.com/embed/${id}?rel=0&playsinline=1` : displaySrc;
            })()}
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            style={{
              maxWidth: hasMultipleImages ? (window.innerWidth >= 768 ? 'calc(100% - 80px)' : 'calc(100% - 50px)') : '100%',
              maxHeight: '100%',
              width: '100%',
              height: '100%',
              minHeight: '400px'
            }}
          />
        ) : isVideoFile(displaySrc) ? (
          <video
            src={displaySrc}
            className="object-contain rounded-lg shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            controls
            playsInline
            preload="metadata"
            style={{
              maxWidth: hasMultipleImages ? (window.innerWidth >= 768 ? 'calc(100% - 80px)' : 'calc(100% - 50px)') : '100%',
              maxHeight: '100%',
              width: 'auto',
              height: 'auto'
            }}
          >
            您的瀏覽器不支援影片播放。
          </video>
        ) : (
          <img
            src={displaySrc}
            alt={displayAlt}
            className="object-contain rounded-lg shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            style={{
              maxWidth: hasMultipleImages ? (window.innerWidth >= 768 ? 'calc(100% - 80px)' : 'calc(100% - 50px)') : '100%',
              maxHeight: '100%',
              width: 'auto',
              height: 'auto'
            }}
          />
        )}
      </div>

      {/* 底部計數器 - 絕對定位在底部中央 */}
      {hasMultipleImages && (
        <div 
          className="absolute bottom-4 left-1/2 transform -translate-x-1/2 text-white text-sm bg-black bg-opacity-50 px-3 py-1 rounded-full z-20"
          style={{ bottom: '16px' }}
        >
          {currentIndex + 1} / {images.length}
        </div>
      )}
    </div>
  );
}

ImageModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  imageSrc: PropTypes.string.isRequired,
  imageAlt: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  images: PropTypes.arrayOf(PropTypes.shape({
    image: PropTypes.string,
    title: PropTypes.string,
  })),
  currentIndex: PropTypes.number,
  onPrevious: PropTypes.func,
  onNext: PropTypes.func,
};

export default ImageModal; 